import * as styles from "./FieldsetLegend.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Collapsible as BaseCollapsible } from "@base-ui/react/collapsible";
import { Fieldset as BaseFieldset } from "@base-ui/react/fieldset";
import { useFieldContext } from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";

import { useFieldsetContext } from "./Fieldset.context";
import type { FieldsetLegendProps } from "./Fieldset.types";

export function FieldsetLegend({
  icon: CustomIcon,
  align = "left",
  count,
  action,
  className,
  children,
  ...props
}: FieldsetLegendProps): JSX.Element {
  const { variant, collapsible } = useFieldsetContext();
  const { size } = useFieldContext();

  const content = (
    <span className={styles.row}>
      <span className={styles.group({ align })}>
        {CustomIcon && (
          <span className={styles.icon}>
            <Icon name={CustomIcon} size="md" />
          </span>
        )}
        <span className={styles.text({ align })}>{children}</span>
        {count !== undefined && <span className={styles.count}>{count}</span>}
      </span>
      {collapsible && (
        <Icon name="ChevronRight" size="md" className={styles.chevron} />
      )}
    </span>
  );

  return (
    <BaseFieldset.Legend
      data-slot="fieldset-legend"
      className={clsx(
        styles.legend({
          align,
          variant,
          size,
          hasAction: Boolean(action),
        }),
        className,
      )}
      {...props}
    >
      {collapsible ? (
        <BaseCollapsible.Trigger
          data-slot="fieldset-legend-trigger"
          className={styles.trigger}
          render={(props) => <span {...props} />}
          nativeButton={false}
        >
          {content}
        </BaseCollapsible.Trigger>
      ) : (
        content
      )}

      {action && (
        <span data-slot="fieldset-legend-action" className={styles.action}>
          {action}
        </span>
      )}
    </BaseFieldset.Legend>
  );
}
